import { Pagination } from "pepsico-ds";
import { useContext, useEffect, useMemo, useState } from "react";

import { listHistoryLogs } from "../../../graphql/queries/listHistoryLogs";
import { extractCampaignDetails } from "../../../utils/historyLogUtils";
import {
  ListingComponentContext,
  ListingComponentProvider,
} from "../../common/listingComponents/ListingComponentContext";
import ListingComponentWrapper from "../../common/listingComponents/ListingComponentWrapper";
import CampaignTable from "./CampaignTable";
import "./CampaignTable.scss";
import {
  contentJsonSchema,
  contentUiSchema,
  debounceFilterJsonSchema,
  debounceFilterUiSchema,
} from "./mocks";

const ContentTab = () => {
  return <ContentTabWithPagination />;
};

const ContentTabWithPagination = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Calculate offset for current page
  const offset = (currentPage - 1) * pageSize;

  return (
    <ListingComponentProvider
      query={listHistoryLogs}
      fetchPolicy="cache-and-network"
      queryKey="listHistoryLogs"
      initialFilterData={{
        type: "content",
        subType: ["banner-content", "page-content", "survey-quiz-content"],
      }}
      extraVariables={{
        offset: offset,
        limit: pageSize,
      }}
      noPagination={true}
    >
      <ContentListLog
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
      />
    </ListingComponentProvider>
  );
};

export const ContentListLog = ({ currentPage, setCurrentPage, pageSize }) => {
  const { filterData, setFilterData, viewableRecords } = useContext(
    ListingComponentContext
  );
  const [currentSchema, setCurrentSchema] = useState(contentJsonSchema);

  // Initialize UI schema with disabled Sub Type dropdown
  const initialDisabledUiSchema = {
    ...contentUiSchema,
    elements: [
      {
        ...contentUiSchema.elements[0],
        elements: contentUiSchema.elements[0].elements.filter((element) => {
          // Hide the entitySubType field initially
          return element.scope !== "#/properties/entitySubType";
        }),
      },
    ],
  };

  const [currentUiSchema, setCurrentUiSchema] = useState(initialDisabledUiSchema);

  // Filter subtypes based on selected type(s) - handles both single and multiple selection
  useEffect(() => {
    if (filterData && filterData.subType) {
      // Handle both array (multiselect) and string (single select) cases
      const selectedSubTypes = Array.isArray(filterData.subType)
        ? filterData.subType
        : [filterData.subType];

      // Get filtered options based on all selected subTypes
      let entitySubTypeOptions = [];

      selectedSubTypes.forEach((subType) => {
        switch (subType) {
          case "banner-content":
            entitySubTypeOptions.push(
              { id: "hero", displayText: "Hero" },
              { id: "mini", displayText: "Mini" }
            );
            break;
          case "survey-quiz-content":
            entitySubTypeOptions.push(
              { id: "survey", displayText: "Survey" },
              { id: "quiz", displayText: "Quiz" }
            );
            break;
          case "page-content":
            entitySubTypeOptions.push(
              { id: "image", displayText: "Image" },
              { id: "video", displayText: "Video" },
              { id: "text", displayText: "Text" }
            );
            break;
        }
      });

      // Remove duplicates if any
      entitySubTypeOptions = entitySubTypeOptions.filter(
        (option, index, self) => index === self.findIndex((o) => o.id === option.id)
      );

      // Create a new schema with filtered options and enabled entitySubType
      const newSchema = {
        ...contentJsonSchema,
        properties: {
          ...contentJsonSchema.properties,
          entitySubType: {
            ...contentJsonSchema.properties.entitySubType,
            options: entitySubTypeOptions,
            disabled: false, // Explicitly enable the dropdown when subType is selected
          },
        },
      };

      setCurrentSchema(newSchema);

      // Create enabled UI schema - show the entitySubType field
      const enabledUiSchema = {
        ...contentUiSchema,
        elements: [
          {
            ...contentUiSchema.elements[0],
            elements: contentUiSchema.elements[0].elements.map((element) => {
              if (element.scope === "#/properties/entitySubType") {
                return {
                  ...element,
                  disabled: false,
                };
              }
              return element;
            }),
          },
        ],
      };
      setCurrentUiSchema(enabledUiSchema);

      // Clear entitySubType if it's not valid for the new subType selection
      if (filterData.entitySubType) {
        const currentEntitySubTypes = Array.isArray(filterData.entitySubType)
          ? filterData.entitySubType
          : [filterData.entitySubType];

        const validEntitySubTypes = currentEntitySubTypes.filter((entitySubType) =>
          entitySubTypeOptions.some((option) => option.id === entitySubType)
        );

        if (validEntitySubTypes.length !== currentEntitySubTypes.length) {
          setFilterData((prev) => ({
            ...prev,
            entitySubType: validEntitySubTypes.length > 0 ? validEntitySubTypes : "",
          }));
        }
      }
    } else {
      // Reset to original schema when no subType is selected and explicitly disable entitySubType
      const disabledSchema = {
        ...contentJsonSchema,
        properties: {
          ...contentJsonSchema.properties,
          entitySubType: {
            ...contentJsonSchema.properties.entitySubType,
            disabled: true, // Explicitly disable the dropdown when no subType is selected
          },
        },
      };
      setCurrentSchema(disabledSchema);

      // Create disabled UI schema - hide the entitySubType field
      const disabledUiSchema = {
        ...contentUiSchema,
        elements: [
          {
            ...contentUiSchema.elements[0],
            elements: contentUiSchema.elements[0].elements.filter((element) => {
              // Hide the entitySubType field when no subType is selected
              return element.scope !== "#/properties/entitySubType";
            }),
          },
        ],
      };
      setCurrentUiSchema(disabledUiSchema);

      // Clear any existing entitySubType values when no subType is selected
      if (filterData?.entitySubType) {
        setFilterData((prev) => ({
          ...prev,
          entitySubType: "",
        }));
      }
    }
  }, [filterData?.subType, filterData, setFilterData]);

  useEffect(() => {
    setFilterData((prev) => ({
      ...prev,
    }));
  }, [setFilterData]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  // Filter the content list based on selected entitySubType using useMemo
  const filteredContentList = useMemo(() => {
    const contentList = viewableRecords?.items || [];
    return contentList.filter((item) => {
      // If no entitySubType is selected, show all items
      if (!filterData?.entitySubType) {
        return true;
      }

      // Extract entitySubType from the item data
      const { entitySubType } = extractCampaignDetails(item?.entity) || {};
      // If entitySubType is not available, exclude the item when filtering
      if (!entitySubType) {
        return false;
      }
      // Handle both single selection and multiple selection
      const selectedEntitySubTypes = Array.isArray(filterData.entitySubType)
        ? filterData.entitySubType
        : [filterData.entitySubType];
      // Check if the item's entitySubType matches any of the selected ones (case-insensitive)
      const matches = selectedEntitySubTypes.some(
        (selectedType) => selectedType.toLowerCase() === entitySubType.toLowerCase()
      );
      return matches;
    });
  }, [viewableRecords?.items, filterData?.entitySubType]);

  // Get the current page items based on filtering and pagination
  const currentPageItems = useMemo(() => {
    // If we're using API pagination (no local filtering), just return the filteredContentList
    if (!filterData?.entitySubType) {
      return filteredContentList;
    }

    // For local filtering, apply pagination manually
    const startIndex = (currentPage - 1) * pageSize;
    return filteredContentList.slice(startIndex, startIndex + pageSize);
  }, [filteredContentList, currentPage, pageSize, filterData?.entitySubType]);

  // Calculate total pages based on filtered content list instead of API response
  const totalFilteredPages = Math.ceil(filteredContentList.length / pageSize);
  // Original total pages from API response (for unfiltered data)
  const totalPages = Math.ceil((viewableRecords?.total || 0) / pageSize);

  // Determine which page count to use based on whether we're filtering
  const effectivePageCount = filterData?.entitySubType
    ? totalFilteredPages
    : totalPages;

  // If filtering resulted in fewer pages than the current page, reset to page 1
  useEffect(() => {
    if (effectivePageCount > 0 && currentPage > effectivePageCount) {
      setCurrentPage(1);
    }
  }, [filterData?.entitySubType, effectivePageCount, currentPage, setCurrentPage]);

  return (
    <div className="px-3 pt-3">
      <div className="container-card">
        <div className="page-content">
          <ListingComponentWrapper
            customStyles={{ flex: 1 }}
            filterClasses="filter-styles"
            offsetBasedPagination={false}
            filterConfig={{
              withDebounce: {
                jsonSchema: debounceFilterJsonSchema,
                uiSchema: debounceFilterUiSchema,
              },
              withoutDebounce: {
                jsonSchema: currentSchema,
                uiSchema: currentUiSchema,
              },
            }}
            toggleViewType={false}
            showAllFiltersOption={true}
            enableAllFiltersButton={false}
          >
            <CampaignTable
              data={
                filterData?.entitySubType ? currentPageItems : filteredContentList
              }
              source="content"
              filterData={filterData}
            />
            {effectivePageCount > 1 && (
              <Pagination
                currentPage={currentPage}
                onUpdate={handleChangePage}
                pageCount={effectivePageCount}
                size="medium"
                variant="number"
              />
            )}
          </ListingComponentWrapper>
        </div>
      </div>
    </div>
  );
};

export default ContentTab;
